import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockNfaLfaService } from '@/services/mock/MockNfaLfaService';
import { LetterForAward } from '@/types/domain';
import { StatusBadge } from '@/components/data-display/StatusBadge';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  ArrowLeft,
  Award,
  Building2,
  Calendar,
  CheckCircle2,
  Printer,
  Truck,
  ShieldCheck,
  FileText,
  Clock,
  ArrowRight,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';

const formatInr = (val: number) =>
  `₹${Number(val || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export interface LfaDetailsPageProps {
  recordId?: string;
  isDrawer?: boolean;
  onClose?: () => void;
}

export const LfaDetailsPage: React.FC<LfaDetailsPageProps> = ({
  recordId,
  isDrawer,
}) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = recordId || paramId;
  const navigate = useNavigate();
  const [lfa, setLfa] = useState<LetterForAward | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    mockNfaLfaService
      .getLfaById(id)
      .then((data) => setLfa(data))
      .finally(() => setIsLoading(false));
  }, [id]);

  if (isLoading) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        Loading Official Letter for Award (LFA) Dossier...
      </div>
    );
  }

  if (!lfa) {
    return (
      <div className="p-16 text-center space-y-4 font-poppins">
        <h2 className="text-lg font-bold text-foreground">LFA Record Not Found</h2>
        <Button size="sm" onClick={() => navigate(ROUTES.LFA.LIST)} className="font-poppins cursor-pointer">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to LFA Ledger
        </Button>
      </div>
    );
  }

  return (
    <div className={`space-y-4 font-poppins ${isDrawer ? 'pb-2' : 'pb-24 max-w-6xl mx-auto'}`}>
      {/* 1. Header Navigation & Actions Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-4">
        <div className="flex items-center gap-3">
          {!isDrawer && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.LFA.LIST)}
              className="rounded-xl border-border hover:bg-muted font-poppins cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to LFA Ledger
            </Button>
          )}
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs font-bold text-primary px-2.5 py-1 bg-primary/10 rounded-lg border border-primary/20">
              {lfa.lfaNumber}
            </span>
            <StatusBadge
              status={lfa.status}
              category="LFA"
              className="w-32 py-1 justify-center text-center font-bold text-xs rounded-full shadow-2xs font-poppins"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.print()}
            className="rounded-xl font-poppins cursor-pointer"
          >
            <Printer className="h-4 w-4 mr-1.5" /> Print Award
          </Button>
          {lfa.status === 'Awarded' && (
            <Button
              size="sm"
              onClick={() => navigate(ROUTES.PURCHASE_ORDERS.NEW || ROUTES.PURCHASE_ORDERS.LIST)}
              className="rounded-xl font-bold font-poppins cursor-pointer bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs"
            >
              Release PO <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          )}
        </div>
      </div>

      {/* 2. Executive Overview & Commercial Commitment Card */}
      <Card className="rounded-2xl border border-border bg-card p-6 shadow-xs space-y-5 font-poppins">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 border-b border-border/60 pb-5">
          <div className="space-y-1.5 max-w-3xl">
            <span className="text-[10px] font-bold uppercase tracking-wider text-primary font-poppins flex items-center gap-1.5">
              <Award className="h-3.5 w-3.5" /> Official Commercial Award Notice
            </span>
            <h1 className="text-xl sm:text-2xl font-extrabold text-foreground font-poppins tracking-tight">
              {lfa.title}
            </h1>
            <p className="text-xs text-foreground-secondary font-medium leading-relaxed font-poppins">
              Binding commercial instrument formally authorizing supplier mobilization, manufacturing capacity allocation, and pre-PO delivery fulfillment.
            </p>
          </div>

          <div className="p-4 rounded-xl bg-primary/5 border border-primary/15 text-left lg:text-right shrink-0">
            <span className="text-[10px] font-bold uppercase tracking-wider text-foreground-muted block font-poppins">
              Awarded Contract Commitment
            </span>
            <span className="text-2xl sm:text-3xl font-black text-primary font-mono block">
              {formatInr(lfa.awardedValue)}
            </span>
            <span className="text-[10px] text-foreground-muted block font-mono mt-0.5">
              Strictly fixed and firm commercial terms
            </span>
          </div>
        </div>

        {/* Metadata Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 pt-1 text-xs">
          <div>
            <span className="text-foreground-muted block text-[10px] font-semibold">Awarded Supplier</span>
            <span className="font-bold text-foreground flex items-center gap-1 truncate">
              <Building2 className="h-3.5 w-3.5 text-primary shrink-0" />
              {lfa.vendorName}
            </span>
            <span className="text-[10px] text-foreground-muted font-mono block">
              ID: {lfa.vendorCode}
            </span>
          </div>

          <div>
            <span className="text-foreground-muted block text-[10px] font-semibold">Linked Approvals</span>
            <span
              onClick={() => navigate(ROUTES.NFA.DETAILS(lfa.nfaId || lfa.nfaNumber))}
              className="font-mono font-bold text-primary hover:underline cursor-pointer block"
            >
              {lfa.nfaNumber}
            </span>
            <span className="text-[10px] text-foreground-muted font-mono block">
              REQ: {lfa.requestNumber}
            </span>
          </div>

          <div>
            <span className="text-foreground-muted block text-[10px] font-semibold">Agreed Payment Terms</span>
            <span className="font-semibold text-foreground block">
              {lfa.paymentTerms || 'Net 30 Days Post-GRN'}
            </span>
            <span className="text-[10px] text-foreground-muted block">
              Invoiced in INR (₹)
            </span>
          </div>

          <div>
            <span className="text-foreground-muted block text-[10px] font-semibold">Delivery Incoterms</span>
            <span className="font-semibold text-foreground flex items-center gap-1">
              <Truck className="h-3.5 w-3.5 text-primary shrink-0" />
              {lfa.deliveryTerms || 'DDP'}
            </span>
            <span className="text-[10px] text-foreground-muted block">
              Delivered Duty Paid
            </span>
          </div>

          <div>
            <span className="text-foreground-muted block text-[10px] font-semibold">Contract Validity</span>
            <span className="font-semibold text-foreground flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 text-foreground-muted shrink-0" />
              {new Date(lfa.effectiveDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
            <span className="text-[10px] text-rose-500 font-mono block">
              Expires: {new Date(lfa.expiryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          </div>
        </div>
      </Card>

      {/* 3. Formal Corporate Letter for Award Document View */}
      <Card className="rounded-2xl border border-border bg-card shadow-xs overflow-hidden font-poppins">
        <CardHeader className="p-4 border-b border-border/60 bg-muted/20">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <FileText className="h-4 w-4 text-primary" />
              Official Letter for Award (Formal Recital & Notice)
            </CardTitle>
            <span className="text-xs text-foreground-muted font-mono font-medium font-poppins">
              REF: {lfa.lfaNumber} / PROC-LEGAL
            </span>
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-5 text-xs text-foreground leading-relaxed font-poppins">
          {/* Recipient Address */}
          <div className="border-b border-border/60 pb-4">
            <span className="text-foreground-muted block text-[10px] font-bold uppercase tracking-wider">
              Issued To:
            </span>
            <h3 className="text-base font-extrabold text-foreground mt-0.5 font-poppins">
              {lfa.vendorName}
            </h3>
            <p className="text-foreground-secondary text-xs font-mono mt-0.5">
              Vendor Code: {lfa.vendorCode} • Quotation Reference: {lfa.quotationId}
            </p>
          </div>

          {/* Letter Body */}
          <div className="space-y-3 font-poppins">
            <p className="font-bold text-sm text-foreground">
              Subject: Commercial Award & Notice to Mobilize — {lfa.title}
            </p>
            <p className="text-foreground-secondary leading-relaxed">
              We are pleased to inform you that following the technical and commercial evaluation of Request for Quotation (<strong>{lfa.rfqNumber || 'RFQ-2001'}</strong>) and executive sanction under Note for Approval (<strong>{lfa.nfaNumber}</strong>), your quotation has been officially selected and awarded.
            </p>
            <p className="text-foreground-secondary leading-relaxed">
              This Letter for Award serves as formal authorization to initiate manufacturing, mobilize raw materials, and reserve production capacity in accordance with the specifications detailed in your commercial proposal.
            </p>
          </div>

          {/* Binding Highlights Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 rounded-xl bg-muted/30 border border-border/80 text-xs">
            <div>
              <span className="text-foreground-muted block text-[10px] font-bold uppercase">Awarded Total Value</span>
              <span className="font-mono font-bold text-sm text-primary block mt-0.5">
                {formatInr(lfa.awardedValue)}
              </span>
            </div>
            <div>
              <span className="text-foreground-muted block text-[10px] font-bold uppercase">Payment Settlement</span>
              <span className="font-bold text-foreground text-xs block mt-0.5">
                {lfa.paymentTerms || 'Net 30 Days'}
              </span>
            </div>
            <div>
              <span className="text-foreground-muted block text-[10px] font-bold uppercase">Delivery Terms</span>
              <span className="font-bold text-foreground text-xs block mt-0.5">
                {lfa.deliveryTerms || 'DDP Plant Delivery'}
              </span>
            </div>
          </div>

          {/* Pre-PO Conditions */}
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-2 text-xs">
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 font-bold">
              <CheckCircle2 className="h-4 w-4" />
              <span>Mandatory Pre-PO Mobilization Deliverables</span>
            </div>
            <ul className="list-disc list-inside text-foreground-secondary space-y-1 pl-1 text-[11.5px]">
              <li>Submit signed and stamped copy of this Letter for Award within 5 business days.</li>
              <li>Furnish 10% Performance Bank Guarantee (PBG) valid until final GRN warranty completion.</li>
              <li>Confirm final manufacturing dispatch milestone schedule and factory inspection dates.</li>
            </ul>
          </div>

          {/* Signature Block */}
          <div className="pt-4 border-t border-border/60 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <span className="text-[10px] text-foreground-muted font-bold uppercase block">Authorized Corporate Signatory</span>
              <span className="font-bold text-foreground text-sm block mt-0.5">
                {lfa.issuedByName || 'David Sterling'}
              </span>
              <span className="text-xs text-foreground-muted block">
                Head of Strategic Sourcing & Procurement
              </span>
            </div>

            <div className="sm:text-right">
              <span className="text-[10px] text-foreground-muted font-bold uppercase block">Effective Award Date</span>
              <span className="font-mono font-bold text-foreground text-xs block mt-0.5">
                {new Date(lfa.effectiveDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 4. Balanced 50/50 Lower Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 font-poppins">
        {/* Left (50%): Traceable Procurement Lifecycle */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Connected End-to-End Procurement Lifecycle
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            <div className="p-3 rounded-xl border border-border bg-muted/20 flex items-center justify-between">
              <div>
                <span className="text-[10px] font-bold uppercase text-foreground-muted block">1. Employee Requisition</span>
                <span className="font-mono font-bold text-primary text-xs">{lfa.requestNumber}</span>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-600">
                ✓ Sanctioned
              </span>
            </div>

            <div className="p-3 rounded-xl border border-border bg-muted/20 flex items-center justify-between">
              <div>
                <span className="text-[10px] font-bold uppercase text-foreground-muted block">2. Commercial Note for Approval</span>
                <span className="font-mono font-bold text-primary text-xs">{lfa.nfaNumber}</span>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-600">
                ✓ Approved
              </span>
            </div>

            <div className="p-3 rounded-xl border border-border bg-muted/20 flex items-center justify-between">
              <div>
                <span className="text-[10px] font-bold uppercase text-foreground-muted block">3. Multi-Vendor RFQ Sourcing</span>
                <span className="font-mono font-bold text-primary text-xs">{lfa.rfqNumber || 'RFQ-2001'}</span>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-600">
                ✓ Awarded
              </span>
            </div>

            <div className="p-3 rounded-xl border border-primary/40 bg-primary/5 flex items-center justify-between ring-1 ring-primary/20">
              <div>
                <span className="text-[10px] font-bold uppercase text-primary block">4. Official Letter for Award</span>
                <span className="font-mono font-bold text-primary text-xs">{lfa.lfaNumber}</span>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-primary text-primary-foreground">
                Active Commitment
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Right (50%): Vendor Mobilization & Compliance Checklist */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <Clock className="h-4 w-4 text-primary" />
              Pre-PO Compliance & Acceptance Status
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3 text-xs">
            <div className="flex items-center justify-between p-3 rounded-xl border border-border bg-muted/15">
              <div className="flex items-center gap-2.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                <span className="font-medium text-foreground">Commercial Bid Price Lock Verified</span>
              </div>
              <span className="text-[10px] font-bold text-emerald-600">Complete</span>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl border border-border bg-muted/15">
              <div className="flex items-center gap-2.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                <span className="font-medium text-foreground">Technical Specification Conformance</span>
              </div>
              <span className="text-[10px] font-bold text-emerald-600">Complete</span>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl border border-border bg-muted/15">
              <div className="flex items-center gap-2.5">
                <Clock className="h-4 w-4 text-amber-500 shrink-0" />
                <span className="font-medium text-foreground">Countersigned LFA Acknowledgment</span>
              </div>
              <span className="text-[10px] font-bold text-amber-600">Pending Receipt</span>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl border border-border bg-muted/15">
              <div className="flex items-center gap-2.5">
                <Clock className="h-4 w-4 text-amber-500 shrink-0" />
                <span className="font-medium text-foreground">Performance Bank Guarantee (PBG 10%)</span>
              </div>
              <span className="text-[10px] font-bold text-amber-600">14 Days Due</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
